// deps
import React, { ReactElement, memo, useMemo } from "react";

// internal-deps
import { ScreenTransition } from "@swiggy-private/core-ui";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";

// hooks
import usePaymentModesState from "@payment-client/hooks/UsePaymentModes";
import { useJuspaySdkState } from "@payment-client/hooks/UseJuspaySdkState";

// contexts
import { PaymentModesContext, PaymentModesContextValue } from "@payment-client/contexts/PaymentModesContext";
import { JuspayContext } from "@payment-client/contexts/JuspayContext";

// constants
import { DEFAULT_JUSPAY_MERCHANTID } from "@payment-client/constants/Juspay";
import { GetAllCardsResponse } from "@payment-client/interfaces/CardApi";
import { TransactionContextProvider } from "./components/transaction-context-provider";
import useForward from "@payment-client/hooks/UseForward";
import { VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-types";
import { getJuspaySdkFeatureAvailability } from "@payment-client/helpers/JuspayHelpers";

interface WithContextProvidersProps {
    getAllCardsResponse: GetAllCardsResponse;
    children: React.ReactNode;
}

const WithContextProviders = ({ children, getAllCardsResponse }: WithContextProvidersProps): React.ReactElement => {
    // juspay
    // recheck with proper values
    const { isSdkAvailable, featuresAvailability, clientAuthTokenData } = useJuspaySdkState(
        "CHECKOUT",
        getAllCardsResponse.merchantInfo.merchantId || DEFAULT_JUSPAY_MERCHANTID,
        true,
    );

    const canUseSdk = getJuspaySdkFeatureAvailability(
        VALID_PAYMENT_GROUP_TYPES.CARD,
        isSdkAvailable,
        featuresAvailability,
    );

    const juspayContextValue = { isSdkAvailable: canUseSdk, featuresAvailability, clientAuthTokenData };

    // TODO: add penny order context
    return (
        <JuspayContext.Provider value={juspayContextValue}>
            <TransactionContextProvider>{children}</TransactionContextProvider>
        </JuspayContext.Provider>
    );
};

export const PaymentModes = memo(function PaymentModes(props: RouteProps): ReactElement {
    const [paymentModeState, reloadPaymentModes] = usePaymentModesState();
    const paymentModesContextValue = useMemo<PaymentModesContextValue>(() => {
        const { getAllCardsResponse = null } =
            paymentModeState.data
                ?.map<Pick<PaymentModesContextValue, "getAllCardsResponse">>((res) => {
                    return {
                        getAllCardsResponse: res.getAllCardsResponse,
                    };
                })
                .unwrapOr({}) ?? {};

        return {
            getAllCardsResponse,
            reloadPaymentModes,
            loadingPaymentModes: paymentModeState.loading,
        };
    }, [paymentModeState.data, paymentModeState.loading, reloadPaymentModes]);

    const forward = useForward();
    const children =
        Array.isArray(props.children) && props.children.length === 1 ? (
            <ScreenTransition forward={forward}>{props.children[0]}</ScreenTransition>
        ) : (
            props.children
        );

    const { loadingPaymentModes, getAllCardsResponse } = paymentModesContextValue;

    return (
        <PaymentModesContext.Provider value={paymentModesContextValue}>
            {loadingPaymentModes || getAllCardsResponse == null ? (
                children
            ) : (
                <WithContextProviders getAllCardsResponse={getAllCardsResponse}>{children}</WithContextProviders>
            )}
        </PaymentModesContext.Provider>
    );
});
